import styled from "styled-components";
import { Box, Text, Image as StyledImage } from "rebass/styled-components";
import Image from "next/image";
import { useState } from "react";
import Carousel, { ModalGateway, Modal, CommonProps } from "react-images";
import Icon from "../common/Icon";
import Trans from "next-translate/Trans";
import { useIsMobile } from "../../lib/media";

const modalStyles = {
  positioner: base => ({
    ...base,
    zIndex: 5
  }),
  blanket: base => ({
    ...base,
    zIndex: 5,
    backgroundColor: "rgba(255,255,255, 0.9)"
  })
};

const carouselStyles = {
  header: base => ({
    ...base,
    background: "none!important"
  }),
  headerClose: base => ({
    ...base,
    color: "#000",
    ":hover": { color: "#000" }
  }),
  headerFullscreen: base => ({
    ...base,
    color: "#000",
    ":hover": { color: "#000" }
  }),
  view: base => ({
    ...base,
    backgroundColor: "none"
  }),
  pager: base => ({
    ...base,
    backgroundColor: "none"
  }),
  footer: base => ({
    ...base,
    background: "none!important",
    color: "#666"
  })
};

const NavigationButton = styled.span`
  position: absolute;
  top: 50%;
  display: block;
  cursor: pointer;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 1em;
  line-height: 48px;
  text-align: center;
  color: ${({ theme }) => theme.colors.springGreen};
  border: ${({ theme }) => theme.borders.default};
  border-color: ${({ theme }) => theme.colors.springGreen};
  margin: -25px 0 0 0;
  left: ${({ type, theme }) =>
    type === "prev" ? `${theme.space[3]}px` : "auto"};
  right: ${({ type, theme }) =>
    type === "next" ? `${theme.space[3]}px` : "auto"};
`;

const NavigationPrev = ({ innerProps, isModal }: any) => {
  return (
    <>
      <NavigationButton {...innerProps} type="prev">
        <Icon name="prev" color="springGreen" />
      </NavigationButton>
    </>
  );
};

const NavigationNext = ({ innerProps, isModal }: any) => (
  <>
    <NavigationButton {...innerProps} type="next">
      <Icon name="next" color="springGreen" />
    </NavigationButton>
  </>
);

interface Photo {
  slider: string;
  thumb: string;
  title: string;
}

interface Props {
  limit: number;
  photos: Photo[];
  imagePriority: boolean; // set to true to boost image load performance when gallery is above the fold
}

const Footer = ({
  innerProps,
  currentIndex,
  currentView,
  views,
  carouselProps
}: CommonProps) => {
  const { setIndex } = carouselProps;

  const isMobile = useIsMobile();

  return (
    <Box
      {...innerProps}
      sx={{
        position: "absolute",
        mx: 1,
        my: 1,
        textAlign: "center",
        bottom: 0,
        right: 3,
        left: 3
      }}
    >
      <Text>
        {currentIndex + 1}/{views.length} {currentView.caption}
      </Text>

      {!isMobile && (
        <Box
          sx={{
            my: 2,
            overflowX: "auto",
            py: 1,
            WebkitOverflowScrolling: "touch",
            whiteSpace: "nowrap",
            mx: [0, 0, 5]
          }}
        >
          {views.map((view, index) => {
            return (
              <Box
                key={index}
                sx={{
                  opacity: currentIndex != index ? 0.5 : 1,
                  boxSizing: "border-box",
                  display: "inline-block",
                  mx: 1,
                  overflow: "hidden",
                  position: "relative",
                  ":hover": {
                    opacity: 0.9
                  }
                }}
              >
                <StyledImage
                  alt={view.caption}
                  src={view.source.regular}
                  sx={{
                    height: "65px",
                    objectFit: "cover"
                  }}
                  onClick={() => {
                    setIndex(index);
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const Gallery = (props: Props) => {
  const { photos, limit, imagePriority } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);

  const photosForCarousel = photos.map(p => ({
    source: { regular: p.slider, fullscreen: p.slider },
    caption: p.title
  }));

  return (
    <Box
      sx={{
        mx: [-2, -2, 0],
        position: "relative",
        overflow: "hidden",
        height: [`calc(61.33vw + ${(100 / 3) * 0.64}vw)`, "auto"]
      }}
    >
      {photos.map((photo, index) => {
        return index < limit ? (
          <Box
            key={index}
            sx={{
              float: "left",
              position: "relative",
              paddingTop: [`${(100 / 3) * 0.64}%`, `${100 / 6.7}%`],
              cursor: "pointer",
              width: [`${100 / 3}%`, "25%"],
              ":first-of-type": {
                paddingTop: ["61.33vw", `${100 / 3.35}%`],
                width: ["100%", "50%"]
              }
            }}
            onClick={() => {
              setSelectedIndex(index);
              setLightboxIsOpen(!lightboxIsOpen);
            }}
          >
            <Image
              src={photo.slider}
              alt={photo.title}
              priority={imagePriority}
              fill
              sizes="100vw"
              style={{
                objectFit: "cover"
              }} />
          </Box>
        ) : null;
      })}

      <ModalGateway>
        {lightboxIsOpen ? (
          <Modal
            allowFullscreen={false}
            styles={modalStyles}
            onClose={() => {
              setSelectedIndex(0);
              setLightboxIsOpen(!lightboxIsOpen);
            }}
          >
            <Carousel
              styles={carouselStyles}
              currentIndex={selectedIndex}
              views={photosForCarousel}
              components={{
                NavigationPrev: NavigationPrev,
                NavigationNext: NavigationNext,
                Footer: Footer
              }}
              setIndex={index => {
                setSelectedIndex(index);
              }}
            />
          </Modal>
        ) : null}
      </ModalGateway>

      {photos.length >= limit ? (
        <>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              paddingTop: [`${(100 / 3) * 0.64}%`, `${100 / 6.7}%`],
              backgroundColor: "rgba(0,0,0,0.8)",
              cursor: "pointer",
              width: [
                0, //`${100/3}%`,
                "25%"
              ]
            }}
            onClick={() => {
              setSelectedIndex(limit - 1);
              setLightboxIsOpen(!lightboxIsOpen);
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: [1, 2],
                left: 0,
                right: 0
              }}
            >
              <Text fontSize={2} color="samoyedWhite" textAlign="center">
                <Trans
                  i18nKey="common:photos"
                  values={{ total: photos.length }}
                />
              </Text>
              <Text
                sx={{
                  color: "springGreen",
                  fontSize: 3,
                  fontWeight: "book",
                  textAlign: "center"
                }}
              >
                <Trans i18nKey="common:seeAll" />
              </Text>
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              paddingTop: [`${(100 / 3) * 0.64}%`, `${100 / 6.7}%`],
              backgroundColor: "rgba(0,0,0,0.8)",
              cursor: "pointer",
              width: [`${100 / 3}%`, 0]
            }}
            onClick={() => {
              setSelectedIndex(limit - 2);
              setLightboxIsOpen(!lightboxIsOpen);
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: [1, 2],
                left: 0,
                right: 0
              }}
            >
              <Text fontSize={2} color="samoyedWhite" textAlign="center">
                <Trans
                  i18nKey="common:photos"
                  values={{ total: photos.length }}
                />
              </Text>
              <Text
                sx={{
                  color: "springGreen",
                  fontSize: 3,
                  fontWeight: "book",
                  textAlign: "center"
                }}
              >
                <Trans i18nKey="common:seeAll" />
              </Text>
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

Gallery.defaultProps = {
  limit: 5,
  imagePriority: false
};

export default Gallery;
